import { useState } from "react";
import { Button, Badge } from "flowbite-react";
import { HiPencil, HiTrash } from "react-icons/hi";
import { EventRecord } from "@/sharedTypes";
import DataTable from "@/components/Table";
import { useUpdateEventMutation, useDeleteEventMutation } from "@/api/locationApi/event";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";
import EventFormDialog from "./EventFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";
import { useTranslation } from "react-i18next";

interface EventListTableProps {
  data: EventRecord[];
  pagination: any;
  onPageChange: (page: number) => void;
}

export default function EventListTable({ data, pagination, onPageChange }: EventListTableProps) {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const user = useCurrentUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventRecord | undefined>();

  const [updateEvent] = useUpdateEventMutation();
  const [deleteEvent] = useDeleteEventMutation();

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  } | null>(null);

  const handleEdit = (event: EventRecord) => {
    setSelectedEvent({
      ...event,
      district_ids: event.districts?.map((d) => d.id) || [],
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (data: EventRecord) => {
    if (!selectedEvent) return;
    try {
      await updateEvent({ ...data, id: selectedEvent.id } as EventRecord).unwrap();
      toast.success(t("temporalActivity:event.updatedSuccess"));
      setIsEditModalOpen(false);
      setSelectedEvent(undefined);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = (event: EventRecord) => {
    setConfirmDialog({
      open: true,
      title: t("temporalActivity:event.confirmDeleteTitle"),
      description: t("temporalActivity:event.confirmDeleteBody"),
      onConfirm: async () => {
        try {
          await deleteEvent(event.id).unwrap();
          toast.success(t("temporalActivity:event.deletedSuccess"));
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
        setConfirmDialog(null);
      },
    });
  };

  const columns = [
    { key: "title", label: t("temporalActivity:event.eventTitle") },
    {
      key: "event_type",
      label: t("temporalActivity:event.eventType"),
      render: (row: EventRecord) => <span>{row.event_type?.event_type ?? "—"}</span>,
    },
    {
      key: "event_status",
      label: t("temporalActivity:event.eventStatus"),
      render: (row: EventRecord) => (
        <Badge color={row.event_status?.status === "cancelled" ? "failure" : "success"} className="w-fit">
          {row.event_status?.status ?? "—"}
        </Badge>
      ),
    },
    {
      key: "location",
      label: t("temporalActivity:event.city"),
      render: (row: EventRecord) => {
        const districtNames = row.districts?.map((d) => d.district_name).join(", ");
        return <span>{districtNames || row.city?.city_name || "—"}</span>;
      },
    },
    {
      key: "dates",
      label: t("temporalActivity:event.dates"),
      render: (row: EventRecord) => (
        <span>
          {row.start_date}
          {row.end_date && row.end_date !== row.start_date ? ` – ${row.end_date}` : ""}
        </span>
      ),
    },
    ...(isCompanyAdminUser(user)
      ? [
          {
            key: "actions",
            label: t("temporalActivity:actions"),
            render: (row: EventRecord) => (
              <div className="flex gap-2">
                <Button size="xs" color="light" onClick={() => handleEdit(row)}>
                  <HiPencil className="h-4 w-4 text-purple-600" />
                </Button>
                <Button size="xs" color="light" onClick={() => handleDelete(row)}>
                  <HiTrash className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <DataTable columns={columns} data={data} pagination={pagination} onPageChange={onPageChange} />

      <EventFormDialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        defaultValues={selectedEvent}
        mode="edit"
      />

      {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          body={confirmDialog.description}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
        />
      )}
    </>
  );
}
